/**
 * ─────────────────────────────────────────────────────────────────────────────
 * AvatarService - Merkezi Avatar ve Yuz Crop Yardimci Servisi
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Tum uygulama genelinde kullanilacak avatar islemleri:
 *   - Profil resmi URL olusturma
 *   - Yuz tanima bbox'ından crop (CSS ve Canvas)
 *   - Bas harf avatar uretimi
 *   - Cinsiyet bazli renk/gradient
 *   - Guven skoru hesaplama ve renklendirme
 *
 * Kullanim:
 *   private avatar = inject(AvatarService);
 *   const url = this.avatar.profilResimUrl('abc123', '128x128');
 *   const stil = this.avatar.yuzCropStili([0.2, 0.1, 0.3, 0.4]);
 *   const harfler = this.avatar.basHarfleri('Ahmet', 'Yilmaz');
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Injectable } from '@angular/core';
import { FikoYTEnvIstemci } from '@/src/service/Environment/environment';

// ─────────────────────────────────────────────────────────────────────────────
// Arayuzler
// ─────────────────────────────────────────────────────────────────────────────

/** Avatar boyut secenekleri */
export type AvatarBoyut = 'sm' | 'md' | 'lg' | 'xl';

/** Cinsiyet kodu */
export type CinsiyetKodu = 'E' | 'K' | 'erkek' | 'kadin' | 'kadın' | string;

/** Normalize edilmis bbox [x, y, w, h] - 0 ile 1 arasi degerler */
export type NormalBbox = [number, number, number, number];

/** CSS stil nesnesi (ngStyle icin uyumlu) */
export interface ICssStil {
  [anahtar: string]: string;
}

/** Avatar bilgi paketi - bir bilesen icin gerekli tum avatar verisi */
export interface IAvatarBilgi {
  basHarfleri: string;
  arkaplanRenk: string;
  gradient: string;
  boyutStili: ICssStil;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sabitler
// ─────────────────────────────────────────────────────────────────────────────

/** Cinsiyet renkleri */
const ERKEK_RENK = '#1890ff';
const KADIN_RENK = '#eb2f96';
const VARSAYILAN_RENK = '#8c8c8c';

/** Cinsiyet gradientleri */
const ERKEK_GRADIENT = 'linear-gradient(135deg, #dbeafe, #93c5fd)';
const KADIN_GRADIENT = 'linear-gradient(135deg, #fce7f3, #f9a8d4)';
const VARSAYILAN_GRADIENT = 'linear-gradient(135deg, #f0f0f0, #d9d9d9)';

/** Guven renkleri */
const GUVEN_YUKSEK_RENK = '#52c41a';   // Yesil  (>80%)
const GUVEN_ORTA_RENK = '#faad14';     // Turuncu (>60%)
const GUVEN_DUSUK_RENK = '#ff4d4f';    // Kirmizi (<=60%)

/** Guven mesafe sinir degerleri */
const GUVEN_MESAFE_MIN = 0;     // %100 guven
const GUVEN_MESAFE_MAX = 1.2;   // %0 guven

/** Avatar boyut haritalari (piksel) */
const BOYUT_HARITASI: Record<AvatarBoyut, { boyut: string; yazi: string; agirlik: string }> = {
  sm: { boyut: '32px', yazi: '14px', agirlik: '600' },
  md: { boyut: '48px', yazi: '18px', agirlik: '600' },
  lg: { boyut: '64px', yazi: '24px', agirlik: '700' },
  xl: { boyut: '96px', yazi: '36px', agirlik: '700' },
};

/** Bbox varsayilan paylama carpani (crop bolgesi etrafina eklenen bosluk) */
const BBOX_PAD_CARPAN = 0.15;

/** Bbox varsayilan zoom carpani (yuz bolgesine zoom seviyesi) */
const BBOX_ZOOM_CARPAN = 1.3;

// ─────────────────────────────────────────────────────────────────────────────
// Servis
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AvatarService {

  // ─────────────────────────────────────────────────────────────────────────
  // 1. Profil Resmi URL Uretici
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Sunucudaki profil resmi icin tam URL olusturur.
   *
   * @param resimId   - GridFS dosya ID'si
   * @param boyut     - Thumbnail boyutu (orn: '96x96', '128x128', '256x256')
   * @param bustCache - true ise URL'e `&_t={Date.now()}` eklenir; aynı id ile
   *                    yeniden yuklenen bir resmin tarayici onbelleginden degil,
   *                    sunucudan taze cekilmesini saglar.
   * @returns Tam resim URL'i veya bos string (resimId yoksa)
   *
   * @example
   * const url = avatar.profilResimUrl('64f1a2b3c4d5e6f7', '128x128');
   * // → "https://api.fikoyt.com/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.64f1a2b3c4d5e6f7.resim?boyut=128x128&thumbnail=true"
   *
   * const tazeUrl = avatar.profilResimUrl('64f1a2b3c4d5e6f7', '128x128', true);
   * // → "...&thumbnail=true&_t=1735689600000"
   */
  profilResimUrl(resimId: string, boyut = '96x96', bustCache = false): string {
    if (!resimId || resimId.trim() === '') return '';

    const fapiUrl = this.fapiUrlAl();
    if (!fapiUrl) return '';

    // NOT: fapiUrl (bkz. environment.ts) zaten ".../fapiv1" ile bitiyor —
    // burada ikinci kez "/fapiv1" eklemek "/fapiv1/fapiv1" ile sonuçlanıp
    // sunucudan 404 dönmesine (resmin hiç görünmemesine) sebep oluyordu.
    const temelUrl = `${fapiUrl}/fikoyt.com.FikoYT.sistem.dosyalar.${resimId}.resim?boyut=${boyut}&thumbnail=true`;
    return bustCache ? `${temelUrl}&_t=${Date.now()}` : temelUrl;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 2. Yuz Crop URL Uretici
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Bbox koordinatlariyla birlikte crop parametreli resim URL'i olusturur.
   * Backend crop destegine baglidir; desteklenmiyorsa `yuzCropStili()` ile
   * CSS tarafinda crop yapilabilir.
   *
   * @param resimId - GridFS dosya ID'si
   * @param bbox    - Normalize bbox [x, y, w, h] (0-1 arasi)
   * @param boyut   - Thumbnail boyutu
   * @returns Crop parametreli resim URL'i
   *
   * @example
   * const url = avatar.yuzCropUrl('abc123', [0.2, 0.1, 0.3, 0.4], '128x128');
   * // → "...resim?boyut=128x128&thumbnail=true&crop=0.2,0.1,0.3,0.4"
   */
  yuzCropUrl(resimId: string, bbox: number[], boyut = '128x128'): string {
    const temelUrl = this.profilResimUrl(resimId, boyut);
    if (!temelUrl) return '';

    if (!bbox || bbox.length < 4) return temelUrl;

    // Bbox degerlerini 0-1 arasinda sinirla
    const guvenli = bbox.map(d => Math.max(0, Math.min(1, d)));
    return `${temelUrl}&crop=${guvenli.join(',')}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 3. Yuz Crop CSS Stili (bbox → CSS object-position + object-fit)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Bbox'tan CSS stili uretir. Bu stil, <img> etiketine [ngStyle] ile
   * uygulandiginda yuz bolgesini merkezleyip zoom yapar.
   *
   * @param bbox       - Normalize bbox [x, y, w, h] (0-1 arasi)
   * @param zoomCarpan - Ek zoom miktari (varsayilan: 1.3)
   * @returns ngStyle uyumlu CSS objesi
   *
   * @example
   * <img [src]="resimUrl" [ngStyle]="avatar.yuzCropStili([0.3, 0.2, 0.2, 0.25])" />
   *
   * Sonuc: {
   *   'object-fit': 'none',
   *   'object-position': '40% 32.5%',
   *   'transform': 'scale(3.85)',
   *   'transform-origin': '40% 32.5%'
   * }
   */
  yuzCropStili(bbox: number[], zoomCarpan = BBOX_ZOOM_CARPAN): ICssStil {
    if (!bbox || bbox.length < 4) {
      return { 'object-fit': 'cover' };
    }

    const [x, y, w, h] = bbox;

    // Gecerlilik kontrolu
    if (w <= 0 || h <= 0) {
      return { 'object-fit': 'cover' };
    }

    // Yuzun merkez noktasi (yuzde olarak)
    const merkezX = (x + w / 2) * 100;
    const merkezY = (y + h / 2) * 100;

    // Zoom orani: bbox ne kadar kucukse o kadar cok zoom
    // w=0.2 icin: 1/0.2 = 5, carpanla: 5 * 1.3 = 6.5 (cok yakin)
    // w=0.5 icin: 1/0.5 = 2, carpanla: 2 * 1.3 = 2.6 (orta)
    const enBuyukBoyut = Math.max(w, h);
    const zoom = (1 / enBuyukBoyut) * zoomCarpan;

    // Zoom'u makul aralikta sinirla (1.2x - 8x)
    const sinirliZoom = Math.max(1.2, Math.min(8, zoom));

    return {
      'object-fit': 'none',
      'object-position': `${merkezX.toFixed(1)}% ${merkezY.toFixed(1)}%`,
      'transform': `scale(${sinirliZoom.toFixed(2)})`,
      'transform-origin': `${merkezX.toFixed(1)}% ${merkezY.toFixed(1)}%`,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 4. Bas Harf Uretici
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Isim ve soyisimden bas harfleri uretir.
   * Tek isim varsa tek harf, iki isim varsa iki harf dondurur.
   * Bos veya gecersiz giriste '?' dondurur.
   *
   * @param isim    - Kisi adi
   * @param soyisim - Kisi soyadi (opsiyonel)
   * @returns Bas harfler (1-2 karakter) veya '?'
   *
   * @example
   * basHarfleri('Ahmet', 'Yilmaz') // → 'AY'
   * basHarfleri('Ahmet')            // → 'A'
   * basHarfleri('Ahmet Mehmet')     // → 'AM' (bosluktan ayirir)
   * basHarfleri('')                 // → '?'
   * basHarfleri(null as any)        // → '?'
   */
  basHarfleri(isim: string, soyisim?: string): string {
    if (!isim && !soyisim) return '?';

    const temizIsim = (isim || '').trim();
    const temizSoyisim = (soyisim || '').trim();

    if (!temizIsim && !temizSoyisim) return '?';

    // Soyisim ayri parametre olarak verildiyse
    if (temizIsim && temizSoyisim) {
      return (temizIsim[0] + temizSoyisim[0]).toUpperCase();
    }

    // Tek string verildiyse bosluktan ayir
    const parcalar = temizIsim.split(/\s+/).filter(p => p.length > 0);
    if (parcalar.length === 0) return '?';

    if (parcalar.length === 1) {
      return parcalar[0][0].toUpperCase();
    }

    // Ilk ve son kelimenin bas harfleri
    return (parcalar[0][0] + parcalar[parcalar.length - 1][0]).toUpperCase();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 5. Cinsiyet Rengi
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Cinsiyet koduna gore renk dondurur.
   *
   * @param cinsiyet - 'E'|'erkek' → mavi, 'K'|'kadin'|'kadın' → pembe
   * @returns Hex renk kodu
   *
   * @example
   * cinsiyetRengi('E')      // → '#1890ff'
   * cinsiyetRengi('kadin')  // → '#eb2f96'
   * cinsiyetRengi('')       // → '#8c8c8c'
   */
  cinsiyetRengi(cinsiyet: CinsiyetKodu): string {
    const kod = (cinsiyet || '').toString().toLowerCase().trim();

    if (kod === 'e' || kod === 'erkek') return ERKEK_RENK;
    if (kod === 'k' || kod === 'kadin' || kod === 'kadın') return KADIN_RENK;

    return VARSAYILAN_RENK;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 6. Cinsiyet Gradient
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Cinsiyet koduna gore CSS gradient string'i dondurur.
   * Kart arkaplan, profil header gibi alanlarda kullanilir.
   *
   * @param cinsiyet - Cinsiyet kodu
   * @returns CSS linear-gradient string'i
   *
   * @example
   * cinsiyetGradient('E')  // → 'linear-gradient(135deg, #dbeafe, #93c5fd)'
   * cinsiyetGradient('K')  // → 'linear-gradient(135deg, #fce7f3, #f9a8d4)'
   */
  cinsiyetGradient(cinsiyet: CinsiyetKodu): string {
    const kod = (cinsiyet || '').toString().toLowerCase().trim();

    if (kod === 'e' || kod === 'erkek') return ERKEK_GRADIENT;
    if (kod === 'k' || kod === 'kadin' || kod === 'kadın') return KADIN_GRADIENT;

    return VARSAYILAN_GRADIENT;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 7. Avatar Stili (nz-avatar icin ngStyle objesi)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * nz-avatar veya ozel avatar bileseni icin tam stil objesi dondurur.
   * Cinsiyet bazli arkaplan rengi, yazi boyutu ve agirligini icerir.
   *
   * @param cinsiyet - Cinsiyet kodu
   * @param boyut    - Avatar boyutu ('sm'|'md'|'lg'|'xl')
   * @returns ngStyle uyumlu CSS objesi
   *
   * @example
   * <nz-avatar [ngStyle]="avatar.avatarStili('E', 'lg')" nzText="AY"></nz-avatar>
   */
  avatarStili(cinsiyet: CinsiyetKodu, boyut: AvatarBoyut = 'md'): ICssStil {
    const renk = this.cinsiyetRengi(cinsiyet);
    const boyutBilgi = BOYUT_HARITASI[boyut] || BOYUT_HARITASI['md'];

    return {
      'background-color': renk,
      'color': '#ffffff',
      'font-size': boyutBilgi.yazi,
      'font-weight': boyutBilgi.agirlik,
      'width': boyutBilgi.boyut,
      'height': boyutBilgi.boyut,
      'line-height': boyutBilgi.boyut,
      'display': 'inline-flex',
      'align-items': 'center',
      'justify-content': 'center',
      'border-radius': '50%',
      'user-select': 'none',
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 8. Guven Yuzdesi (mesafe → yuzde)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Yuz tanima mesafesini guven yuzdesine cevirir.
   * Mesafe 0 ise %100, mesafe 1.2 ise %0 dondurur.
   * Aradaki degerler lineer olarak haritalanir.
   *
   * @param mesafe - Yuz tanima mesafesi (0 = tam eslesme, 1.2 = hic eslesmez)
   * @returns Guven yuzdesi (0-100 arasi tam sayi)
   *
   * @example
   * guvenYuzde(0)    // → 100
   * guvenYuzde(0.3)  // → 75
   * guvenYuzde(0.6)  // → 50
   * guvenYuzde(1.2)  // → 0
   * guvenYuzde(-0.1) // → 100 (sinirda tutar)
   */
  guvenYuzde(mesafe: number): number {
    if (mesafe == null || isNaN(mesafe)) return 0;

    // Mesafeyi gecerli araliga sinirla
    const sinirli = Math.max(GUVEN_MESAFE_MIN, Math.min(GUVEN_MESAFE_MAX, mesafe));

    // Lineer donusum: 0 → 100, 1.2 → 0
    const yuzde = ((GUVEN_MESAFE_MAX - sinirli) / GUVEN_MESAFE_MAX) * 100;
    return Math.round(yuzde);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 9. Guven Rengi
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Guven yuzdesine gore renk dondurur.
   *   - >80%  → Yesil  (#52c41a) - Yuksek guven
   *   - >60%  → Turuncu (#faad14) - Orta guven
   *   - <=60% → Kirmizi (#ff4d4f) - Dusuk guven
   *
   * @param yuzde - Guven yuzdesi (0-100)
   * @returns Hex renk kodu
   *
   * @example
   * guvenRenk(95)  // → '#52c41a' (yesil)
   * guvenRenk(70)  // → '#faad14' (turuncu)
   * guvenRenk(40)  // → '#ff4d4f' (kirmizi)
   */
  guvenRenk(yuzde: number): string {
    if (yuzde > 80) return GUVEN_YUKSEK_RENK;
    if (yuzde > 60) return GUVEN_ORTA_RENK;
    return GUVEN_DUSUK_RENK;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 10. Canvas ile Yuz Crop (Gelismis)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Canvas API ile resimden yuz bolgesini keser, Blob olarak dondurur.
   * Bu blob daha sonra `URL.createObjectURL()` ile avatar olarak kullanilabilir.
   *
   * Islem adimlari:
   *   1. Resim URL'inden Image nesnesi olusturur
   *   2. OffscreenCanvas (veya fallback Canvas) ile bbox bolgesini keser
   *   3. JPEG Blob olarak dondurur
   *
   * @param resimUrl - Kaynak resim URL'i (CORS uyumlu olmali)
   * @param bbox     - Normalize bbox [x, y, w, h] (0-1 arasi)
   * @param cikisBoyut - Cikti kare boyutu piksel (varsayilan: 128)
   * @returns Kesilen yuz bolgesi Blob nesnesi
   * @throws Resim yuklenemezse veya canvas desteklenmiyorsa hata firlatar
   *
   * @example
   * const blob = await avatar.yuzCropBlob('https://api.../resim.jpg', [0.2, 0.1, 0.3, 0.4]);
   * const objectUrl = URL.createObjectURL(blob);
   * // <img [src]="objectUrl" /> olarak kullanilabilir
   * // Kullanildiktan sonra: URL.revokeObjectURL(objectUrl)
   */
  async yuzCropBlob(resimUrl: string, bbox: number[], cikisBoyut = 128): Promise<Blob> {
    if (!resimUrl) {
      throw new Error('Resim URL\'i bos olamaz');
    }
    if (!bbox || bbox.length < 4) {
      throw new Error('Gecersiz bbox: en az 4 eleman gerekli [x, y, w, h]');
    }

    const [bx, by, bw, bh] = bbox;

    // 1. Resmi yukle
    const img = await this.resimYukle(resimUrl);

    // 2. Bbox piksel degerlerini hesapla (pad ekleyerek)
    const pad = Math.max(bw, bh) * BBOX_PAD_CARPAN;
    const kaynakX = Math.max(0, (bx - pad) * img.width);
    const kaynakY = Math.max(0, (by - pad) * img.height);
    const kaynakW = Math.min(img.width - kaynakX, (bw + pad * 2) * img.width);
    const kaynakH = Math.min(img.height - kaynakY, (bh + pad * 2) * img.height);

    // 3. Canvas olustur ve ciz
    const canvas = document.createElement('canvas');
    canvas.width = cikisBoyut;
    canvas.height = cikisBoyut;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context olusturulamadi');
    }

    // Kare crop: kaynak bolgeyi kare cikisa sigdir
    ctx.drawImage(
      img,
      kaynakX, kaynakY, kaynakW, kaynakH,  // kaynak bolge
      0, 0, cikisBoyut, cikisBoyut,          // hedef bolge
    );

    // 4. Blob'a donustur
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas blob donusumu basarisiz'));
          }
        },
        'image/jpeg',
        0.85,  // Kalite: %85
      );
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 11. Yardimci / Birlestirici Metodlar
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Bir kisi icin komple avatar bilgi paketi olusturur.
   * Tek bir cagriyla tum avatar verilerini alir.
   *
   * @param isim     - Kisi adi
   * @param soyisim  - Kisi soyadi
   * @param cinsiyet - Cinsiyet kodu
   * @param boyut    - Avatar boyutu
   * @returns Tam avatar bilgi paketi
   *
   * @example
   * const bilgi = avatar.avatarBilgiPaketi('Ahmet', 'Yilmaz', 'E', 'lg');
   * // → { basHarfleri: 'AY', arkaplanRenk: '#1890ff', gradient: '...', boyutStili: {...} }
   */
  avatarBilgiPaketi(
    isim: string,
    soyisim?: string,
    cinsiyet?: CinsiyetKodu,
    boyut: AvatarBoyut = 'md',
  ): IAvatarBilgi {
    return {
      basHarfleri: this.basHarfleri(isim, soyisim),
      arkaplanRenk: this.cinsiyetRengi(cinsiyet || ''),
      gradient: this.cinsiyetGradient(cinsiyet || ''),
      boyutStili: this.avatarStili(cinsiyet || '', boyut),
    };
  }

  /**
   * Guven mesafesinden tam guven gosterge verisi olusturur.
   * Yuzde ve rengi tek bir cagriyla dondurur.
   *
   * @param mesafe - Yuz tanima mesafesi
   * @returns { yuzde, renk, etiket } nesnesi
   *
   * @example
   * const g = avatar.guvenBilgi(0.25);
   * // → { yuzde: 79, renk: '#faad14', etiket: '79%' }
   */
  guvenBilgi(mesafe: number): { yuzde: number; renk: string; etiket: string } {
    const yuzde = this.guvenYuzde(mesafe);
    return {
      yuzde,
      renk: this.guvenRenk(yuzde),
      etiket: `${yuzde}%`,
    };
  }

  /**
   * Resim ID ve bbox bilgisiyle kullanima hazir avatar verisi olusturur.
   * Resim varsa URL + crop stili, yoksa bas harf + renk dondurur.
   *
   * @param params - Avatar kaynak parametreleri
   * @returns Kullanima hazir avatar verisi
   *
   * @example
   * const veri = avatar.avatarKaynagi({
   *   resimId: 'abc123',
   *   bbox: [0.2, 0.1, 0.3, 0.4],
   *   isim: 'Ahmet',
   *   soyisim: 'Yilmaz',
   *   cinsiyet: 'E',
   *   boyut: 'lg'
   * });
   * // Resim varsa:  { tip: 'resim', url: '...', cropStili: {...} }
   * // Resim yoksa:  { tip: 'harf',  basHarfleri: 'AY', stil: {...} }
   */
  avatarKaynagi(params: {
    resimId?: string;
    bbox?: number[];
    isim?: string;
    soyisim?: string;
    cinsiyet?: CinsiyetKodu;
    boyut?: AvatarBoyut;
    resimBoyut?: string;
  }): { tip: 'resim'; url: string; cropStili: ICssStil } | { tip: 'harf'; basHarfleri: string; stil: ICssStil } {
    const { resimId, bbox, isim, soyisim, cinsiyet, boyut = 'md', resimBoyut = '128x128' } = params;

    // Resim varsa resim bazli avatar
    if (resimId && resimId.trim()) {
      const url = this.profilResimUrl(resimId, resimBoyut);
      const cropStili = bbox && bbox.length >= 4
        ? this.yuzCropStili(bbox)
        : { 'object-fit': 'cover' as string };

      return { tip: 'resim', url, cropStili };
    }

    // Resim yoksa harf bazli avatar
    return {
      tip: 'harf',
      basHarfleri: this.basHarfleri(isim || '', soyisim),
      stil: this.avatarStili(cinsiyet || '', boyut),
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 12. Clip-Path ile Yuz Crop (CSS-only alternatif)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Bbox'tan CSS clip-path stili uretir.
   * Canvas gerektirmez, tamamen CSS bazli crop saglar.
   * `yuzCropStili()` yerine daha kesin kesim istenen yerlerde kullanilir.
   *
   * @param bbox - Normalize bbox [x, y, w, h] (0-1 arasi)
   * @returns ngStyle uyumlu CSS objesi (clip-path + transform iceren)
   *
   * @example
   * <img [src]="resimUrl" [ngStyle]="avatar.clipPathStili([0.3, 0.2, 0.2, 0.25])" />
   */
  clipPathStili(bbox: number[]): ICssStil {
    if (!bbox || bbox.length < 4) {
      return {};
    }

    const [x, y, w, h] = bbox;
    if (w <= 0 || h <= 0) return {};

    // Padding ekle (yuz etrafinda bosluk)
    const pad = Math.max(w, h) * BBOX_PAD_CARPAN;
    const solUst_x = Math.max(0, x - pad) * 100;
    const solUst_y = Math.max(0, y - pad) * 100;
    const sagAlt_x = Math.min(1, x + w + pad) * 100;
    const sagAlt_y = Math.min(1, y + h + pad) * 100;

    // clip-path: inset(top right bottom left)
    const ust = solUst_y;
    const sag = 100 - sagAlt_x;
    const alt = 100 - sagAlt_y;
    const sol = solUst_x;

    return {
      'clip-path': `inset(${ust.toFixed(1)}% ${sag.toFixed(1)}% ${alt.toFixed(1)}% ${sol.toFixed(1)}%)`,
      'object-fit': 'cover',
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Dahili Yardimci Metodlar
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * FikoYT ortam yapilandirmasindan API URL'ini alir.
   *
   * 🐛 FIX: Eskiden yalnizca `window.FikoYT?.env?.adres?.fapiUrl` / `window.FikoYT?.fapiUrl`
   * okunuyordu — ama `window.FikoYT` uygulamanin hicbir yerinde set edilmiyor (grep ile
   * dogrulandi). Sonuc: fapiUrl HER ZAMAN bos donuyor, `profilResimUrl()` de HER ZAMAN ''
   * donup <img> hicbir zaman render edilemiyordu (bu, "resim guncelleniyor ama gorunmuyor"
   * sikayetinin asil kok nedeniydi — bileşendeki getter/OnPush duzeltmesi dogruydu ama
   * URL zaten hep bostu). Dogru kaynak: `FikoYTEnvIstemci.adres.fapiUrl` (bkz. fikoyt.service.ts
   * icindeki ayni kullanim). window.FikoYT eski/olasi baska bir entegrasyon icin fallback
   * olarak birakildi.
   */
  private fapiUrlAl(): string {
    const win = window as any;
    return (
      FikoYTEnvIstemci?.adres?.fapiUrl ||
      win?.FikoYT?.env?.adres?.fapiUrl ||
      win?.FikoYT?.fapiUrl ||
      ''
    );
  }

  /**
   * Bir resim URL'inden Image nesnesi yukler.
   * Promise bazli: yukleme tamamlandiginda resolve, hatada reject olur.
   *
   * @param url - Resim URL'i
   * @returns Yuklenmis HTMLImageElement
   */
  private resimYukle(url: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';  // CORS: canvas taint'i onle

      img.onload = () => resolve(img);
      img.onerror = (hata) => reject(new Error(`Resim yuklenemedi: ${url}`));

      img.src = url;
    });
  }
}
